import React, { useState, useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router';
import { parse, stringify } from 'querystring';
import styled from 'styled-components';
import { LinkOutlined } from '@ant-design/icons';
import { Space } from 'antd';
import { useLocalStorageState } from 'ahooks';
import { AccountPersistInfo } from 'Internal.CommonAPI';
import ContainerWrapper from 'Common/Components/ContainerWrapper';
import { Tabs } from 'Common/Components/Tabs';
import { DateRange } from 'Common/types';
import DateRangePicker from 'Reports/components/views/DateRangePicker';
// import RevenueByUsersContainer from 'Reports/components/RevenueByUsers';
import RevenueOverview from 'Reports/components/RevenueOverview';
import LastTransactions from 'Reports/components/LastTransactions';
import SalesReport from 'Reports/components/SalesReport';
import GiftcardsReport from 'Reports/components/GiftcardsReport';
import TipsReport from 'Reports/components/TipsReport';
import InventoryReport from 'Reports/components/InventoryReport';
import EmployeeReport from 'Reports/components/EmployeeReport';

dayjs.extend(utc);

const CustomTabs = styled(Tabs)`
  .ant-tabs-content-holder {
    margin: 0px 14px !important;
  }

  .ant-tabs-tab + .ant-tabs-tab:nth-last-child(3) {
    margin-left: 5px !important;
  }

  .ant-tabs-tab + .ant-tabs-tab:nth-last-child(2) {
    margin-left: 35px !important;
  }
  @media (max-width: 768px) {
    .ant-tabs-nav {
      column-gap: 10px;
      display: flex;
      flex-direction: column;
    }
    .ant-tabs-nav-wrap {
      overflow-x: auto;
    }
    .ant-tabs-extra-content {
      margin: 20px 0 20px 0;
    }
  }
`;

const LiveReports: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { search } = useLocation();
  const query = parse(search.slice(1));
  const queryClient = useQueryClient();

  const [account] = useLocalStorageState<AccountPersistInfo>('account');
  const currency = account?.currency as string;
  const [isGiftcardsTabActive, setIsGiftcardsTabActive] = useState(
    search.includes('?_=3')
  );

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(
    query.start && search !== ''
      ? [
          dayjs.utc(query.start as string),
          dayjs
            .utc(query.end as string)
            .endOf('day')
            .millisecond(0),
        ]
      : [
          dayjs.utc().subtract(1, 'week').add(1, 'day').startOf('day'),
          dayjs.utc().endOf('day').millisecond(0),
        ]
  );

  useEffect(() => {
    if (account?.source === 'shore' && search === '?_=6') {
      navigate('/reports/live?_=1');
    }
  }, [navigate, search, account?.source]);

  const dateHandler = (value: DateRange) => {
    const defaultParam = query._ ? `${String(query._)}` : '1';
    if (!value) return;
    const [start, end] = value;
    const formattedStartDate = start?.format('YYYY-MM-DD');
    const formattedEndDate = end?.format('YYYY-MM-DD');
    const hasDates = formattedStartDate && formattedEndDate;
    const params = stringify({
      _: defaultParam,
      ...(hasDates && { start: formattedStartDate, end: formattedEndDate }),
    });

    setDateRange([start as Dayjs, end as Dayjs]);
    navigate(`/reports/live?${params}`);
    queryClient.removeQueries({
      queryKey: ['reports', 'cards'],
    });
    queryClient.invalidateQueries({
      queryKey: ['reports', 'basic'],
    });
    queryClient.invalidateQueries({
      queryKey: ['reports', 'revenue-payment'],
    });
  };

  const dateSelection = (
    <div style={{ display: 'block' }}>
      <DateRangePicker
        dateRange={dateRange}
        handleDateChange={dateHandler}
        disabled={isGiftcardsTabActive}
      />
    </div>
  );

  return (
    <CustomTabs
      defaultActiveKey="1"
      onChange={(key) => setIsGiftcardsTabActive(key === '3')}
      tabBarExtraContent={dateSelection}
      items={[
        {
          key: '1',
          label: intl.formatMessage({ defaultMessage: 'Dashboard' }),
          children: (
            <Space direction="vertical" css="width: 100%; overflow:auto">
              <ContainerWrapper css="min-height: 100%">
                <RevenueOverview dateRange={dateRange} />
              </ContainerWrapper>
              <ContainerWrapper css="min-height: unset">
                <LastTransactions dateRange={dateRange} currency={currency} />
              </ContainerWrapper>
            </Space>
          ),
        },
        {
          key: '2',
          label: intl.formatMessage({ defaultMessage: 'Sales Report' }),
          children: (
            <ContainerWrapper
              css={{ height: '100%', minHeight: 'unset !important' }}
            >
              <SalesReport
                dateFilter={dateRange}
                currency={currency}
                query={query}
              />
            </ContainerWrapper>
          ),
          css: 'overflow:auto',
        },
        {
          key: '3',
          label: intl.formatMessage({ defaultMessage: 'Giftcards Report' }),
          children: (
            <ContainerWrapper
              css={{
                padding: '1em',
                height: '100%',
                minHeight: 'unset !important',
              }}
            >
              <GiftcardsReport currency={currency} query={query} />
            </ContainerWrapper>
          ),
          css: 'overflow:auto',
        },
        {
          key: '4',
          label: intl.formatMessage({ defaultMessage: 'Inventory Report' }),
          children: (
            <ContainerWrapper
              css={{
                padding: '1em',
                height: '100%',
                minHeight: 'unset !important',
              }}
            >
              <InventoryReport dateFilter={dateRange} query={query} />
            </ContainerWrapper>
          ),
          css: 'overflow:auto',
        },
        account?.isTipsUser && {
          key: '5',
          label: intl.formatMessage({ defaultMessage: 'Tip Report' }),
          children: (
            <TipsReport
              dateFilter={dateRange}
              currency={currency}
              query={query}
            />
          ),
          css: 'overflow:auto',
        },
        account?.source === 'shore' && {
          key: '6',
          label: (
            <a
              href="https://my.shore.com/pos-reports-v2/pos/"
              target="_blank"
              rel="noopener noreferrer"
              css="position: absolute;
              left: 0;
              width: 100%;
              height: 100%;
              "
            >
              <LinkOutlined css="padding-right: 12px" />
              {intl.formatMessage({ defaultMessage: 'Calendar Reports' })}
            </a>
          ),
          children: null,
          css: 'position: relative',
        },
        // account?.isBetaUser && {
        //   key: '7',
        //   label: intl.formatMessage({ defaultMessage: 'Employee Reports' }),
        //   children: (
        //    <ContainerWrapper css={{ padding: '1em', height: '100%' }}>
        // <RevenueByUsersContainer />
        // </ContainerWrapper>
        //   ),
        //   css: 'overflow:auto',
        // },
        {
          key: '8',
          label: intl.formatMessage({ defaultMessage: 'Employee Reports' }),
          children: (
            <ContainerWrapper css={{ padding: '1em', height: '100%' }}>
              <EmployeeReport dateFilter={dateRange} query={query} />
            </ContainerWrapper>
          ),
          css: 'overflow:auto',
        },
      ]}
    />
  );
};

export default LiveReports;
